/**
 * Plan catalogue and entitlement resolution.
 *
 * IMPORTANT: this file is the single source of truth for what a plan may do.
 * It is imported by the Worker (authoritative enforcement) and by the browser
 * (purely for rendering). The browser copy is never trusted - every protected
 * endpoint re-resolves entitlements server-side from the database.
 */

import type { Entitlements, PlanId, SubscriptionStatus } from './types';

const KB = 1024;
const MB = 1024 * KB;

/**
 * Free plan is intentionally narrow so that the product can run inside
 * Cloudflare + Supabase free tiers.
 */
export const FREE_ENTITLEMENTS: Entitlements = {
  planId: 'free',
  displayName: 'Free',
  priceMonthlyUsd: 0,
  monthlyDiagnoses: 5,
  maxInputBytes: 100 * KB,
  maxSavedSpecs: 1,
  historyRetentionDays: 7,
  persistentHistory: false,
  // Free users may save one spec and get structural feedback on it.
  // Deep request/response validation is a Pro feature (see docs/architecture.md).
  openApiValidation: 'structural',
  shareableReports: false,
  exportFormats: ['markdown'],
  priorityProcessing: false,
};

export const PRO_ENTITLEMENTS: Entitlements = {
  planId: 'pro',
  displayName: 'Pro',
  priceMonthlyUsd: 9.99,
  monthlyDiagnoses: 100,
  maxInputBytes: 1 * MB,
  maxSavedSpecs: 10,
  historyRetentionDays: null,
  persistentHistory: true,
  openApiValidation: 'full',
  shareableReports: true,
  exportFormats: ['markdown', 'json'],
  priorityProcessing: true,
};

export const PLAN_CATALOG: Record<PlanId, Entitlements> = {
  free: FREE_ENTITLEMENTS,
  pro: PRO_ENTITLEMENTS,
};

/** Stripe statuses that keep Pro access switched on. */
const ACTIVE_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  'active',
  'trialing',
]);

/**
 * Statuses that mean "was Pro, payment is failing". We keep Pro switched on for
 * the paid period (Stripe retries) but surface the problem in the UI.
 */
const GRACE_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  'past_due',
  'unpaid',
  'incomplete',
  'paused',
]);

export interface EntitlementInput {
  status: SubscriptionStatus | null | undefined;
  currentPeriodEnd: string | null | undefined;
  cancelAtPeriodEnd?: boolean | null;
  /** Injected for deterministic tests. */
  now?: Date;
}

export interface EntitlementResolution {
  entitlements: Entitlements;
  planId: PlanId;
  /** True when the account should be treated as paid right now. */
  proActive: boolean;
  /** True when Stripe reports a payment problem on an otherwise Pro account. */
  paymentIssue: boolean;
  reason: string;
}

/**
 * Resolve entitlements from a *verified* subscription row.
 *
 * The subscription row is written only by the Stripe webhook handler, so this
 * function is never fed browser-supplied plan data.
 */
export function resolveEntitlements(input: EntitlementInput): EntitlementResolution {
  const now = input.now ?? new Date();
  const status = (input.status ?? 'none') as SubscriptionStatus;

  if (status === 'none') {
    return {
      entitlements: FREE_ENTITLEMENTS,
      planId: 'free',
      proActive: false,
      paymentIssue: false,
      reason: 'no_subscription',
    };
  }

  // Cancelled subscriptions only lose Pro once the paid period has elapsed.
  if (status === 'canceled') {
    const end = input.currentPeriodEnd ? new Date(input.currentPeriodEnd) : null;
    const stillInPeriod =
      end !== null && !Number.isNaN(end.getTime()) && end.getTime() > now.getTime();
    if (stillInPeriod) {
      return {
        entitlements: PRO_ENTITLEMENTS,
        planId: 'pro',
        proActive: true,
        paymentIssue: false,
        reason: 'canceled_but_within_paid_period',
      };
    }
    return {
      entitlements: FREE_ENTITLEMENTS,
      planId: 'free',
      proActive: false,
      paymentIssue: false,
      reason: 'canceled_period_ended',
    };
  }

  if (ACTIVE_STATUSES.has(status)) {
    return {
      entitlements: PRO_ENTITLEMENTS,
      planId: 'pro',
      proActive: true,
      paymentIssue: false,
      reason: 'active',
    };
  }

  if (GRACE_STATUSES.has(status)) {
    const end = input.currentPeriodEnd ? new Date(input.currentPeriodEnd) : null;
    const withinPeriod =
      end !== null && !Number.isNaN(end.getTime()) && end.getTime() > now.getTime();
    // `paused` means the subscription is not collecting payment, so Pro is off.
    if (status === 'paused' || !withinPeriod) {
      return {
        entitlements: FREE_ENTITLEMENTS,
        planId: 'free',
        proActive: false,
        paymentIssue: true,
        reason: `downgraded_${status}`,
      };
    }
    return {
      entitlements: PRO_ENTITLEMENTS,
      planId: 'pro',
      proActive: true,
      paymentIssue: true,
      reason: `grace_${status}`,
    };
  }

  // `incomplete_expired` and anything unknown means no Pro access.
  return {
    entitlements: FREE_ENTITLEMENTS,
    planId: 'free',
    proActive: false,
    paymentIssue: status === 'incomplete_expired',
    reason: `inactive_${status}`,
  };
}

/** Convenience helper used by UI copy and by the account page. */
export function describePlan(planId: PlanId): string {
  return planId === 'pro' ? 'Pro' : 'Free';
}

export function maxInputBytesFor(planId: PlanId): number {
  return PLAN_CATALOG[planId].maxInputBytes;
}

export function hasEntitlement(
  entitlements: Entitlements,
  feature: 'shareableReports' | 'persistentHistory' | 'priorityProcessing',
): boolean {
  return Boolean(entitlements[feature]);
}
